import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import type { EnrichedPublicShop } from '../../stores/shopStore'
import { useAuthStore } from '../../stores/authStore'
import { apiUrl, posterUrl } from '../../config'
import { getCatStyle } from './categoryStyles'

/**
 * The shop detail card shown when a scan is picked out of a 3D view — a planet
 * tapped in Discover, or a shopfront walked up to in the 3D mall. Both places
 * are doing the same thing (you singled out one shop inside a world, now decide
 * whether to go in), so they share one card rather than each growing their own.
 *
 * `variant` only moves the card: "space" pins it above Discover's bottom tab
 * bar, "mall" sits it lower in the full-screen mall overlay where there is no
 * tab bar to clear.
 */
export default function ScanPanel({ shop, onEnter, onShare, onClose, variant = 'space' }: {
  shop: EnrichedPublicShop
  onEnter: () => void
  onShare: (e: React.MouseEvent) => void
  onClose: () => void
  variant?: 'space' | 'mall'
}) {
  const posterSrc = posterUrl(shop.id)
  const catStyle = getCatStyle(shop.category)
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const token = useAuthStore((s) => s.token)
  const [menuOpen, setMenuOpen] = useState(false)
  const [following, setFollowing] = useState(false)
  const [followLoading, setFollowLoading] = useState(false)

  useEffect(() => {
    if (!user || !token) return
    fetch(apiUrl(`/api/shops/${shop.id}/following`), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : { following: false })
      .then((d) => setFollowing(d.following))
      .catch(() => {})
  }, [shop.id, user, token])

  async function handleFollow(e: React.MouseEvent) {
    e.stopPropagation()
    if (!user || !token) { window.location.href = '/login'; return }
    setFollowLoading(true)
    try {
      if (following) {
        await fetch(apiUrl(`/api/shops/${shop.id}/follow`), { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
        setFollowing(false)
      } else {
        await fetch(apiUrl(`/api/shops/${shop.id}/follow`), { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } })
        setFollowing(true)
      }
    } catch { /* ignore */ } finally { setFollowLoading(false) }
  }

  return (
    <div
      className={`space-scan-panel${variant === 'mall' ? ' space-scan-panel--mall' : ''}`}
      style={{ '--scan-color': catStyle.color, '--scan-rgb': catStyle.rgb } as React.CSSProperties}
      onClick={e => e.stopPropagation()}
    >
      <div className="space-scan-top">
        {/* Discover flags the find; in the mall you already walked up to the
            shop, so announcing "scan detected" states the obvious. */}
        {variant === 'space' && (
          <div className="space-scan-badge">
            <div className="space-scan-ping" />&nbsp;SCAN DETECTED
          </div>
        )}
        <button className="space-scan-close" onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      <div className="space-scan-poster">
        <img src={posterSrc} alt={shop.name} />
        {shop.has3DViewer && <span className="space-scan-live-badge">✦ Live 3D</span>}
      </div>
      <div className="space-scan-body">
        <h2 className="space-scan-name">{shop.name}</h2>
        <div className="space-scan-meta">
          {shop.category && <span className="space-scan-cat">{shop.category}</span>}
          <span className="space-scan-sections">{shop.sections?.length || 0} sections</span>
        </div>
        {shop.address && <p className="space-scan-address">{shop.address}</p>}
      </div>
      <div className="space-scan-actions">
        <button className="space-scan-enter" onClick={onEnter}>
          {variant === 'mall' ? 'Enter Store' : 'Enter World'}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14m-6-6 6 6-6 6"/></svg>
        </button>
        <button className="space-scan-share" onClick={(e) => { e.stopPropagation(); setMenuOpen(true) }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
          </svg>
        </button>
      </div>

      {menuOpen && createPortal(
        <div className="shop-card-modal-overlay" onClick={() => setMenuOpen(false)}>
          <div className="shop-card-modal" onClick={(e) => e.stopPropagation()}>
            <div className="shop-card-modal-header">
              <span className="shop-card-modal-title">{shop.name}</span>
              <button className="shop-card-modal-close" onClick={() => setMenuOpen(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            {shop.has3DViewer && (
              <button className="shop-card-menu-item" onClick={(e) => { setMenuOpen(false); onShare(e) }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                Share
              </button>
            )}
            <button
              className={`shop-card-menu-item${following ? ' shop-card-menu-item--active' : ''}`}
              onClick={async (e) => { setMenuOpen(false); await handleFollow(e) }}
              disabled={followLoading}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                <line x1="12" y1="2" x2="12" y2="4"/>
              </svg>
              {following ? 'Unfollow' : 'Follow'}
            </button>
            <button
              className="shop-card-menu-item"
              onClick={() => { setMenuOpen(false); navigate(`/reviews/${shop.id}`) }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              Reviews
            </button>
            <button
              className="shop-card-menu-item shop-card-menu-item--danger"
              onClick={() => { setMenuOpen(false); window.location.href = `/report?shopId=${shop.id}` }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>
              </svg>
              Report scan
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
